import { useState } from 'react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveCredits } from '@/hooks/useEffectiveCredits';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Smartphone, ChevronDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { isValidDisplayName } from '@/lib/utils';
import { APP_STORE_URL } from '@/lib/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  minimal?: boolean;
}

export function Header({ minimal = false }: HeaderProps) {
  const { user, profile, signOut, isAdmin } = useAuth();
  const { credits: effectiveCredits } = useEffectiveCredits();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isMobile, isIOS } = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isSubscribed = profile?.plan === 'pro' || profile?.plan === 'executive';

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 gradient-dark backdrop-blur-lg border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/">
          <Logo />
        </Link>
        
        {/* Desktop Navigation - Hide when minimal */}
        {!minimal && (
          <nav className="hidden md:flex items-center gap-4">
            {!user && (
              <>
                <Link to="/pricing">
                  <Button variant="ghost" size="sm">
                    Pricing
                  </Button>
                </Link>
                <Link to="/faq">
                  <Button variant="ghost" size="sm">
                    FAQs
                  </Button>
                </Link>
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="sm"
                    className="gradient-gold text-primary-foreground font-semibold hover:opacity-90"
                  >
                    <Smartphone className="w-4 h-4" />
                    Download App
                  </Button>
                </a>
              </>
            )}
            {user ? (
              <>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/50 border border-border/50">
                  <span className="text-sm font-semibold text-primary">{effectiveCredits}</span>
                  <span className="text-sm text-foreground">credits</span>
                </div>
                <Link to="/search">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={isActive('/search') ? 'text-primary' : ''}
                  >
                    Search
                  </Button>
                </Link>
                <Link to="/feed">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={isActive('/feed') ? 'text-primary' : ''}
                  >
                    My Feed
                  </Button>
                </Link>
                <Link to="/library">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={isActive('/library') ? 'text-primary' : ''}
                  >
                    Library
                  </Button>
                </Link>
                {!isSubscribed && (
                  <Link to="/pricing">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className={isActive('/pricing') ? 'text-primary' : ''}
                    >
                      Upgrade
                    </Button>
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center gap-1">
                      {isValidDisplayName(profile?.first_name) ? profile.first_name : 'Account'}
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36 min-w-0">
                    <DropdownMenuItem asChild className="text-xs py-1.5 px-3">
                      <Link to="/analytics" className="w-full cursor-pointer">Analytics</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="text-xs py-1.5 px-3">
                      <Link to="/settings" className="w-full cursor-pointer">Settings</Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild className="text-xs py-1.5 px-3">
                        <Link to="/admin" className="w-full cursor-pointer">Admin</Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-xs py-1.5 px-3">
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : null}
          </nav>
        )}

        {/* Mobile Menu Button - Hide when minimal */}
        {!minimal && (
          <button
            className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        )}
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-lg">
          <nav className="flex flex-col p-4 gap-3">
            {!user && (
              <>
                <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full h-12 justify-center">
                    Pricing
                  </Button>
                </Link>
                <Link to="/faq" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full h-12 justify-center">
                    FAQs
                  </Button>
                </Link>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full h-12 gradient-gold text-primary-foreground font-semibold hover:opacity-90">
                    <Smartphone className="w-4 h-4" />
                    Download App
                  </Button>
                </a>
              </>
            )}
            {user ? (
              <>
                <div className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-secondary/50 border border-border/50">
                  <span className="text-sm font-semibold text-primary">{effectiveCredits}</span>
                  <span className="text-sm text-foreground">credits</span>
                </div>
                <Link to="/search" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full h-12 justify-center">
                    Search
                  </Button>
                </Link>
                <Link to="/feed" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full h-12 justify-center">
                    My Feed
                  </Button>
                </Link>
                <Link to="/library" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full h-12 justify-center">
                    Library
                  </Button>
                </Link>
                {!isSubscribed && (
                  <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      variant="ghost" 
                      className="w-full h-12"
                    >
                      Upgrade
                    </Button>
                  </Link>
                )}
                <Link to="/analytics" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full h-12 justify-center">
                    Analytics
                  </Button>
                </Link>
                <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full h-12 justify-center">
                    Settings
                  </Button>
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full h-12 justify-center">
                      Admin
                    </Button>
                  </Link>
                )}
                {isMobile && !isIOS && (
                  <Link to="/install" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full h-12 justify-center">
                      <Smartphone className="w-4 h-4" />
                      Install App
                    </Button>
                  </Link>
                )}
                <Button
                  variant="ghost" 
                  className="w-full h-12" 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleSignOut();
                  }}
                >
                  Sign Out
                </Button>
              </>
            ) : null}
          </nav>
        </div>
      )}
    </header>
  );
}
